import type { TickKind } from '../data/tickListTemplates';
import type { TickListRun } from './db';

export type CheckState = 'done' | 'due' | 'overdue' | 'never';

export interface CheckStatus {
  state: CheckState;
  lastDone: string | null;
}

function isoWeek(d: Date): number {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

/**
 * Compute the traffic-light status for a tick-list kind given its most recent run.
 * Logic preserved from v8:
 *   - daily: done if any run today, overdue from tomorrow
 *   - weekly / telehandler_weekly: done if same ISO week, due if <=7 days else overdue
 *   - monthly: done if same calendar month, overdue from next month
 */
export function computeCheckStatus(kind: TickKind, runs: TickListRun[]): CheckStatus {
  const last = runs[0]; // runs are ordered DESC by performed_at
  if (!last) return { state: 'never', lastDone: null };

  const lastDate = new Date(last.performed_at);
  const now = new Date();

  if (kind === 'daily') {
    if (lastDate.toDateString() === now.toDateString()) {
      return { state: 'done', lastDone: last.performed_at };
    }
    return { state: 'overdue', lastDone: last.performed_at };
  }

  if (kind === 'weekly' || kind === 'telehandler_weekly') {
    const sameWeek =
      isoWeek(lastDate) === isoWeek(now) && lastDate.getFullYear() === now.getFullYear();
    if (sameWeek) return { state: 'done', lastDone: last.performed_at };
    const days = Math.floor((now.getTime() - lastDate.getTime()) / 86400000);
    return { state: days > 7 ? 'overdue' : 'due', lastDone: last.performed_at };
  }

  if (kind === 'monthly') {
    if (
      lastDate.getMonth() === now.getMonth() &&
      lastDate.getFullYear() === now.getFullYear()
    ) {
      return { state: 'done', lastDone: last.performed_at };
    }
    return { state: 'overdue', lastDone: last.performed_at };
  }

  return { state: 'never', lastDone: null };
}

export function statusLabel(state: CheckState): string {
  return { done: 'Done', due: 'Due now', overdue: 'Overdue', never: 'Not yet done' }[state];
}

export function statusBadgeClass(state: CheckState): string {
  return {
    done: 'badge-success',
    due: 'badge-warn',
    overdue: 'badge-danger',
    never: 'badge-neutral',
  }[state];
}

export function statusDotColor(state: CheckState): string {
  return {
    done: '#16A34A',
    due: '#BF8E3D',
    overdue: '#B0413E',
    never: '#B8C0C5',
  }[state];
}
